import React, { useEffect, useRef, useState } from "react";
import { useFormState } from "react-final-form";
import Skeleton from "react-loading-skeleton";

import { getValuation } from "../api/get-valuation";
import { EditButton } from "../components/EditButton";
import { NavigationButton } from "../components/NavigationButton";
import { ValuationResponse } from "../types/apis";
import { EscalateValuation, FormData, FormResources } from "../types/formData";
import { Navigation } from "../types/navigation";
import { checkForEscalations } from "../utils/checkForEscalations";
import {
  NavigationFooter,
  PageContent,
  ValuationAmount,
} from "../valuation-manager.styles";
import {
  useValuationManagerContext,
  useValuationManagerDispatch,
} from "../ValuationManagerContext";

interface Props {
  setPageName: (pageName: Navigation) => void;
  formResources: FormResources;
  escalateValuation: EscalateValuation;
  submitRetryError: boolean;
}

const haveFormValuesChanged = (
  currentValues: FormData,
  valuesToCompare: FormData,
): boolean => {
  return (
    currentValues.serviceHistory !== valuesToCompare.serviceHistory ||
    currentValues.condition !== valuesToCompare.condition
  );
};

export const SummaryPage = ({
  setPageName,
  formResources,
  escalateValuation,
  submitRetryError,
}: Props) => {
  const [retryError, setRetryError] = useState<boolean>(false);
  const [valuationRequested, setValuationRequested] = useState<boolean>(false);
  const { appInput, valuation, valuationRequestFormValues, vehicleDetails } =
    useValuationManagerContext();
  const { values, submitting }: { values: FormData; submitting: boolean } =
    useFormState();
  const attempts = useRef(0);
  const dispatch = useValuationManagerDispatch();

  useEffect(() => {
    if (
      valuationRequestFormValues &&
      haveFormValuesChanged(values as FormData, valuationRequestFormValues)
    ) {
      if (dispatch) {
        dispatch({
          type: "UpdateValuationManagerContext",
          data: {
            valuation: undefined,
            valuationRequestFormValues: undefined,
          },
        });
      }
    }
  }, [dispatch, values, valuationRequestFormValues]);

  const handleClick = async () => {
    if (!vehicleDetails || !appInput) {
      return;
    }
    setValuationRequested(true);

    const escalationDetails = checkForEscalations(
      values,
      formResources,
      vehicleDetails!.checks,
    );

    try {
      const response: ValuationResponse = await getValuation(
        {
          vrm: appInput.vrm,
          mileage: appInput.mileage,
          condition: values.condition!,
          // `owners` is deliberately not sent. This app was already correct — it
          // subtracted the current keeper from AutoTrader's total — but the
          // valuations service now derives previous owners itself from the same
          // AutoTrader figure, so sending it duplicates a calculation that only
          // has one right answer. Removing it also removes the chance of the two
          // drifting apart. See the owners migration in carsa-valuations-service.
          serviceHistory: values.serviceHistory!,
          user: "staff",
        },
        appInput.jwt,
      );
      if (dispatch) {
        dispatch({
          type: "UpdateValuationManagerContext",
          data: {
            valuation: response,
            valuationRequestFormValues: values as FormData,
          },
        });
      }
      setRetryError(false);

      if (escalationDetails.length > 0) {
        escalateValuation(
          "This vehicle needs to be escalated",
          escalationDetails,
          values,
          response,
        );
      }
    } catch (error: unknown) {
      setValuationRequested(false);
      attempts.current++;
      if (attempts.current === 2) {
        if (error instanceof Error) {
          escalateValuation(
            "Unable to get valuation from API",
            error.message + "\n" + escalationDetails,
            values,
          );
        } else {
          escalateValuation("Unable to get valuation from API", "", values);
        }
      }
      setRetryError(true);
    }
  };

  const conditionLabel = formResources.conditions.options.find(
    (condition) => condition.value === values?.condition,
  )!.label;
  const serviceHistoryLabel = formResources.serviceHistory.options.find(
    (serviceHistory) => serviceHistory.value === values?.serviceHistory,
  )!.label;

  return (
    <div>
      <PageContent>
        <h2>{formResources.summary.title}</h2>
        {retryError && (
          <div className="alert alert-danger">
            Unable to retrieve valuation, please retry
          </div>
        )}
        {submitRetryError && (
          <div className="alert alert-danger">
            Unable to submit valuation, please retry
          </div>
        )}
        <div className="row">
          <div className="col-sm-12 col-md-12 col-lg-12">
            <ol>
              <li>
                Qualification: Passed
                <EditButton
                  onClick={() => setPageName(Navigation.Qualifications)}
                  label="edit"
                  dataTestId="edit-qualifications"
                />
              </li>
              <li>
                Modified: No
                <EditButton
                  onClick={() => setPageName(Navigation.Modifications)}
                  label="edit"
                  dataTestId="edit-modifications"
                />
              </li>
              <li>
                Service History: {`${serviceHistoryLabel}`}{" "}
                <EditButton
                  onClick={() => setPageName(Navigation.ServiceHistory)}
                  label="edit"
                  dataTestId="edit-service-history"
                />
              </li>
              <li>
                Condition: {`${conditionLabel}`}{" "}
                <EditButton
                  onClick={() => setPageName(Navigation.Conditions)}
                  label="edit"
                  dataTestId="edit-conditions"
                />
              </li>
              <li>
                Mechanical and Electrical Faults: No
                <EditButton
                  onClick={() => setPageName(Navigation.MechanicalAndElectrical)}
                  label="edit"
                  dataTestId="edit-mechanical-and-electrical"
                />
              </li>
            </ol>
          </div>
        </div>
        <div className="row">
          <div className="col-sm-2 col-md-2 col-lg-2 col-md-offset-4 col-sm-offset-4 col-lg-offset-4">
            <button
              type="button"
              onClick={handleClick}
              disabled={valuationRequested || !!valuation}
              className="btn btn-primary"
            >
              Get Valuation
            </button>
          </div>

          <div className="col-sm-3 col-md-3 col-lg-3">
            {valuationRequested && !valuation && (
              <ValuationAmount>
                <Skeleton count={1} width="100px" />
              </ValuationAmount>
            )}
            {!!valuation && (
              <>
                <ValuationAmount>
                  Valuation successfully retrieved. Please Save the Valuation.
                </ValuationAmount>
              </>
            )}
            {!valuationRequested && !valuation && (
              <ValuationAmount></ValuationAmount>
            )}
          </div>
        </div>
      </PageContent>
      <NavigationFooter className="row">
        <div className="col-sm-6 col-md-6 col-lg-6"></div>
        <div className="col-sm-6 col-md-6 col-lg-6 text-right">
          <button
            type="submit"
            disabled={!valuation || submitting}
            className="btn btn-primary"
          >
            Save STV Valuation
          </button>
        </div>
      </NavigationFooter>
      <NavigationButton setPageName={setPageName} />
    </div>
  );
};
